import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Layout from '@/components/Layout';
import { toast } from 'sonner';
import { Upload, FileText, CheckCircle, XCircle, Search, Eye, Trash2, Download, CloudOff, Printer, AlertTriangle, Tag, UserCog, Undo2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import offlineDB from '@/utils/offlineDB';
import LabelPrintDialog from './LabelPrintDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Orders({ token, user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('delivery_date'); // delivery_date, uploaded_at, supplier, filename
  const [sortOrder, setSortOrder] = useState('desc'); // asc or desc
  const [isLoading, setIsLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [paperRequirementDialogOpen, setPaperRequirementDialogOpen] = useState(false);
  const [suppliersRequiringPaper, setSuppliersRequiringPaper] = useState([]);
  const [labelPrintDialogOpen, setLabelPrintDialogOpen] = useState(false);
  const [selectedNoteForLabel, setSelectedNoteForLabel] = useState(null);
  const [assignSupplierDialogOpen, setAssignSupplierDialogOpen] = useState(false);
  const [noteToAssign, setNoteToAssign] = useState(null);
  const [assignSupplierId, setAssignSupplierId] = useState('');
  const [featureSettings, setFeatureSettings] = useState({
    qr_label_printing_enabled: true
  });
  const [unsignDialogOpen, setUnsignDialogOpen] = useState(false);
  const [noteToUnsign, setNoteToUnsign] = useState(null);

  useEffect(() => {
    fetchOrders();
    fetchSuppliers();
    fetchFeatureSettings();
    

    // Sync pending signatures when coming online
    syncPendingSignatures();

    // Listen for online event to sync when connection is restored
    const handleOnline = () => {
      console.log('🌐 Connection restored - syncing pending signatures...');
      toast.info('Verbindung wiederhergestellt - Synchronisiere Daten...');
      syncPendingSignatures();
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };

    // Check URL parameters
    const params = new URLSearchParams(location.search);
    const action = params.get('action');
    const supplierId = params.get('supplier');
    const filter = params.get('filter');
    
    if (action === 'upload') {
      setUploadDialogOpen(true);
    }
    
    if (supplierId) {
      setSelectedSupplierId(supplierId);
      setUploadDialogOpen(true);
    }
    
    if (filter === 'signed') {
      setFilterStatus('signed');
    } else if (filter === 'unsigned') {
      setFilterStatus('unsigned');
    }
  }, [location]);

  useEffect(() => {
    let filtered = orders;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(order => {
        const supplier = suppliers.find(c => c.id === order.supplier_id);
        const supplierName = supplier?.company_name || '';
        return (
          order.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
          supplierName.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    // Filter by status
    if (filterStatus === 'signed') {
      filtered = filtered.filter(order => order.signed);
    } else if (filterStatus === 'unsigned') {
      filtered = filtered.filter(order => !order.signed);
    } else if (filterStatus === 'locked') {
      filtered = filtered.filter(order => order.locked === true);
    }

    // Sort
    filtered.sort((a, b) => {
      let compareA, compareB;
      
      switch(sortBy) {
        case 'supplier':
          const supplierA = suppliers.find(c => c.id === a.supplier_id);
          const supplierB = suppliers.find(c => c.id === b.supplier_id);
          compareA = supplierA?.company_name?.toLowerCase() || '';
          compareB = supplierB?.company_name?.toLowerCase() || '';
          break;
        case 'filename':
          compareA = a.filename.toLowerCase();
          compareB = b.filename.toLowerCase();
          break;
        case 'delivery_date':
          // Parse delivery date (DD.MM.YYYY format) to Date object
          const parseDateString = (dateStr) => {
            if (!dateStr) return new Date(0); // Use epoch for missing dates
            const parts = dateStr.split(/[.\-/]/);
            if (parts.length === 3) {
              // DD.MM.YYYY format
              return new Date(parts[2], parts[1] - 1, parts[0]);
            }
            return new Date(0);
          };
          compareA = parseDateString(a.delivery_date);
          compareB = parseDateString(b.delivery_date);
          break;
        case 'uploaded_at':
        default:
          compareA = new Date(a.uploaded_at);
          compareB = new Date(b.uploaded_at);
          break;
      }
      
      if (sortOrder === 'asc') {
        return compareA > compareB ? 1 : -1;
      } else {
        return compareA < compareB ? 1 : -1;
      }
    });

    setFilteredNotes(filtered);
  }, [searchTerm, filterStatus, sortBy, sortOrder, orders, suppliers]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
      setFilteredNotes(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Bestellunge');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Lieferantn');
    }
  };

  const fetchFeatureSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/features`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeatureSettings(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Feature-Einstellungen:', error);
    }
  };

  const syncPendingSignatures = async () => {
    try {
      const pendingSignatures = await offlineDB.getPendingSignatures();
      
      if (pendingSignatures.length === 0) {
        console.log('No pending signatures to sync');
        return;
      }

      console.log(`Syncing ${pendingSignatures.length} pending signature(s)...`);
      let successCount = 0;
      let failCount = 0;

      for (const pending of pendingSignatures) {
        try {
          // Skip if this is a delivery note signature
          if (pending.type !== 'order' && !pending.orderId) {
            console.log(`⏭️ Skipping delivery note signature: ${pending.id}`);
            continue;
          }

          console.log(`🔄 Syncing order signature:`, pending);

          // Parse body if it's stored as JSON string
          let signatureData;
          if (pending.body) {
            signatureData = typeof pending.body === 'string' ? JSON.parse(pending.body) : pending.body;
          } else {
            // Fallback to direct fields
            const orderId = pending.orderId || pending.noteId || pending.id;
            signatureData = {
              order_id: orderId,
              signature_data: pending.signature_data || pending.signatureData,
              signer_name: pending.signer_name || pending.signerName,
              signature_date: pending.signature_date || pending.signatureDate,
              delivery_info: pending.delivery_info
            };
          }

          console.log(`📤 Sending to server:`, signatureData);
          
          // Submit signature to server
          const response = await axios.post(
            `${API}/orders/sign`,
            signatureData,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          console.log(`✅ Server response:`, response.status, response.data);

          // Delete from pending after successful sync
          await offlineDB.deletePendingSignature(pending.id);
          successCount++;
          console.log(`✅ Successfully synced and deleted: ${pending.id}`);
        } catch (error) {
          console.error(`❌ Failed to sync order signature:`, pending.id, error.response?.data || error.message);
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} Unterschrift(en) erfolgreich synchronisiert!`);
        // Refresh delivery notes after sync
        fetchOrders();
      }

      if (failCount > 0) {
        toast.warning(`${failCount} Unterschrift(en) konnten nicht synchronisiert werden.`);
      }
    } catch (error) {
      console.error('Error syncing pending signatures:', error);
    }
  };


  const handlePrintA4 = (order) => {
    // Open PDF in new window for printing
    const pdfWindow = window.open('', '_blank');
    pdfWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${order.filename}</title>
          <style>
            body { margin: 0; padding: 0; }
            iframe { width: 100vw; height: 100vh; border: none; }
          </style>
        </head>
        <body>
          <iframe src="data:application/pdf;base64,${order.pdf_data}"></iframe>
        </body>
      </html>
    `);
    pdfWindow.document.close();
    
    // Auto-trigger print dialog after PDF loads
    setTimeout(() => {
      pdfWindow.print();
    }, 500);
    
    toast.success('PDF wird geöffnet zum Drucken...');
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFiles || selectedFiles.length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Datei aus');
      return;
    }

    setIsLoading(true);
    let successCount = 0;
    let duplicateCount = 0;
    let errorCount = 0;

    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      
      // Check if file already exists
      const existingNote = orders.find(order => order.filename === file.name);
      if (existingNote) {
        toast.warning(`"${file.name}" existiert bereits - übersprungen`);
        duplicateCount++;
        continue;
      }

      const formData = new FormData();
      formData.append('file', file);
      if (selectedSupplierId) {
        formData.append('supplier_id', selectedSupplierId);
      }

      try {
        const response = await axios.post(`${API}/orders/upload`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        successCount++;
        setUploadProgress(prev => ({...prev, [file.name]: 'success'}));
      } catch (error) {
        errorCount++;
        setUploadProgress(prev => ({...prev, [file.name]: 'error'}));
        toast.error(`Fehler bei "${file.name}": ${error.response?.data?.detail || 'Upload fehlgeschlagen'}`);
      }
    }

    // Summary message
    if (successCount > 0) {
      toast.success(`${successCount} Bestellung(e) erfolgreich hochgeladen`);
    }
    if (duplicateCount > 0) {
      toast.info(`${duplicateCount} Duplikat(e) übersprungen`);
    }
    
    setUploadDialogOpen(false);
    setSelectedSupplierId('');
    setSelectedFiles([]);
    setUploadProgress({});
    setIsLoading(false);
    await fetchOrders();

    // Check for suppliers requiring paper delivery notes
    if (successCount > 0) {
      checkForPaperRequirements();
    }
  };

  const getSupplierName = (supplierId) => {
    const supplier = suppliers.find(c => c.id === supplierId);
    return supplier?.company_name || 'Unbekannt';
  };

  const checkForPaperRequirements = () => {
    // Find suppliers who require paper delivery notes
    const suppliersNeedingPaper = suppliers.filter(c => c.requires_paper_order === true);
    
    if (suppliersNeedingPaper.length > 0) {
      setSuppliersRequiringPaper(suppliersNeedingPaper);
      setPaperRequirementDialogOpen(true);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDeleteNote = async () => {
    if (!noteToDelete) return;

    try {
      await axios.delete(`${API}/orders/${noteToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bestellung erfolgreich gelöscht');
      setDeleteDialogOpen(false);
      setNoteToDelete(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const handleUnsignNote = async () => {
    if (!noteToUnsign) return;

    try {
      await axios.post(`${API}/orders/${noteToUnsign.id}/unsign`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Unterschrift erfolgreich rückgängig gemacht');
      setUnsignDialogOpen(false);
      setNoteToUnsign(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Rückgängigmachen');
    }
  };

  const handlePrepareForOffline = async (order) => {
    try {
      // Save delivery order to IndexedDB
      await offlineDB.saveNoteForOffline(order);
      
      // Save associated supplier to IndexedDB if not already saved
      const supplier = suppliers.find(c => c.id === order.supplier_id);
      if (supplier) {
        await offlineDB.saveSupplierForOffline(supplier);
      }
      
      toast.success(`Bestellung "${order.filename}" für Offline-Nutzung vorbereitet`, {
        icon: <Download className="h-5 w-5" />
      });
    } catch (error) {
      console.error('Failed to prepare for offline:', error);
      toast.error('Fehler beim Vorbereiten für Offline-Nutzung');
    }
  };

  const handlePrepareAllForOffline = async () => {
    try {
      setIsLoading(true);
      
      // Save all suppliers
      for (const supplier of suppliers) {
        await offlineDB.saveSupplierForOffline(supplier);
      }
      
      // Save all unsigned orders
      const unsignedNotes = orders.filter(order => !order.signed);
      for (const order of unsignedNotes) {
        await offlineDB.saveNoteForOffline(order);
      }
      
      toast.success(`${unsignedNotes.length} Bestellung(e) für Offline-Nutzung vorbereitet`, {
        icon: <CloudOff className="h-5 w-5" />,
        duration: 4000
      });
    } catch (error) {
      console.error('Failed to prepare for offline:', error);
      toast.error('Fehler beim Vorbereiten für Offline-Nutzung');
    } finally {
      setIsLoading(false);
    }
  };


  const handleAssignSupplier = async (e) => {
    e.preventDefault();
    if (!assignSupplierId || !noteToAssign) return;
    
    try {
      setIsLoading(true);
      await axios.post(
        `${API}/orders/${noteToAssign.id}/assign-supplier`,
        { supplier_id: assignSupplierId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Bestellung erfolgreich zugeordnet!');
      setAssignSupplierDialogOpen(false);
      setNoteToAssign(null);
      setAssignSupplierId('');
      fetchOrders();
    } catch (error) {
      toast.error('Fehler beim Zuordnen des Bestellungs');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="orders">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif', color: '#0A1F33' }}>Bestellunge</h1>
            <p className="text-gray-600 mt-1">PDF-Bestellunge hochladen und verwalten</p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handlePrepareAllForOffline}
              disabled={isLoading || orders.filter(n => !n.signed).length === 0}
              title="Alle nicht unterschriebenen Bestellunge für Offline-Nutzung vorbereiten"
            >
              <CloudOff className="mr-2 h-4 w-4" />
              Für Offline vorbereiten
            </Button>
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button className="metaxon-primary text-white" data-testid="upload-order-btn">
                  <Upload className="mr-2 h-4 w-4" />
                  Bestellung hochladen
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="upload-dialog">
              <DialogHeader>
                <DialogTitle>Bestellung hochladen</DialogTitle>
                <DialogDescription>
                  Laden Sie einen PDF-Bestellung hoch. Der Lieferant und die Projektnummer werden automatisch aus dem PDF erkannt.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpload} className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="pdf-file">PDF-Bestellunge (mehrere möglich) *</Label>
                  <Input
                    id="pdf-file"
                    type="file"
                    accept=".pdf,application/pdf"
                    multiple
                    onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                    required
                    data-testid="pdf-file-input"
                  />
                  {selectedFiles.length > 0 && (
                    <div className="text-xs text-gray-600">
                      {selectedFiles.length} Datei(en) ausgewählt
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Das System erkennt den Lieferantn automatisch aus dem PDF-Inhalt
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="supplier-select">Lieferant manuell auswählen (optional)</Label>
                  <Select value={selectedSupplierId} onValueChange={setSelectedSupplierId}>
                    <SelectTrigger data-testid="supplier-select">
                      <SelectValue placeholder="Automatische Erkennung oder manuell wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.filter(c => c.company_name && c.company_name.trim()).map(supplier => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          {supplier.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    Nur auswählen, wenn automatische Erkennung nicht funktioniert.
                  </p>
                  <p className="text-xs text-amber-600 font-medium">
                    ⚠️ Ohne Lieferant wird der Bestellung als "Nicht zugeordnet" gesperrt.
                  </p>
                </div>
                <Button type="submit" className="w-full metaxon-primary text-white" disabled={isLoading} data-testid="upload-submit-btn">
                  {isLoading ? `Wird hochgeladen (${selectedFiles.length})...` : 'Hochladen'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          <Card className="glass-effect border-0">
            <CardHeader className="pb-3">
              <div className="flex items-center space-x-2">
                <Search className="h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Suchen nach Dateiname oder Lieferant..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="orders-search-input"
                />
              </div>
            </CardHeader>
          </Card>

          {/* Filter Buttons */}
          <Card className="glass-effect border-0">
            <CardHeader>
              <CardTitle className="text-sm font-medium mb-3">Status filtern</CardTitle>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={filterStatus === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('all')}
                  className={filterStatus === 'all' ? 'metaxon-primary text-white' : ''}
                  data-testid="filter-all"
                >
                  <FileText className="mr-2 h-4 w-4" />
                  Alle Bestellunge
                </Button>
                <Button
                  variant={filterStatus === 'signed' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('signed')}
                  className={filterStatus === 'signed' ? 'bg-gradient-to-br from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700' : ''}
                  data-testid="filter-signed"
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Unterschrieben
                </Button>
                <Button
                  variant={filterStatus === 'unsigned' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('unsigned')}
                  className={filterStatus === 'unsigned' ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700' : ''}
                  data-testid="filter-unsigned"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Noch nicht unterschrieben
                </Button>
                <Button
                  variant={filterStatus === 'locked' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterStatus('locked')}
                  className={filterStatus === 'locked' ? 'bg-gradient-to-br from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700' : ''}
                  data-testid="filter-locked"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Nicht zugeordnet
                </Button>
              </div>
            </CardHeader>
          </Card>

          {/* Sort Options */}
          <Card className="glass-effect border-0">
            <CardHeader>
              <CardTitle className="text-sm font-medium mb-3">Sortieren nach</CardTitle>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={sortBy === 'delivery_date' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSortBy('delivery_date')}
                  className={sortBy === 'delivery_date' ? 'metaxon-primary text-white' : ''}
                  data-testid="sort-delivery-date"
                >
                  Bestellungdatum
                </Button>
                <Button
                  variant={sortBy === 'uploaded_at' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSortBy('uploaded_at')}
                  className={sortBy === 'uploaded_at' ? 'metaxon-primary text-white' : ''}
                  data-testid="sort-upload-date"
                >
                  Upload-Datum
                </Button>
                <Button
                  variant={sortBy === 'supplier' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSortBy('supplier')}
                  className={sortBy === 'supplier' ? 'metaxon-primary text-white' : ''}
                  data-testid="sort-supplier"
                >
                  Lieferant
                </Button>
                <Button
                  variant={sortBy === 'filename' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSortBy('filename')}
                  className={sortBy === 'filename' ? 'metaxon-primary text-white' : ''}
                  data-testid="sort-filename"
                >
                  Dateiname
                </Button>
                <div className="border-l mx-2"></div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  data-testid="sort-order"
                >
                  {sortOrder === 'asc' ? '↑ Aufsteigend' : '↓ Absteigend'}
                </Button>
              </div>
            </CardHeader>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredNotes.length === 0 ? (
            <Card className="col-span-full glass-effect border-0">
              <CardContent className="pt-6 text-center">
                <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">
                  {searchTerm || filterStatus !== 'all' ? 'Keine Bestellunge gefunden' : 'Noch keine Bestellunge vorhanden.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            filteredNotes.map((order) => (
              <Card key={order.id} className="glass-effect border-0 hover:shadow-lg transition-shadow" data-testid={`order-card-${order.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg bg-gradient-to-br ${order.signed ? 'from-green-500 to-green-600' : 'from-orange-500 to-orange-600'}`}>
                        {order.signed ? <CheckCircle className="h-5 w-5 text-white" /> : <XCircle className="h-5 w-5 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-base truncate" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            {order.filename}
                          </CardTitle>
                          {order.locked && (
                            <span className="px-2 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded">
                              GESPERRT
                            </span>
                          )}
                        </div>
                        <CardDescription className="text-xs">
                          {order.locked ? (
                            order.detected_supplier_name ? (
                              <span>Lieferant (aus PDF): <span className="font-semibold">{order.detected_supplier_name}</span></span>
                            ) : (
                              'Lieferant unbekannt'
                            )
                          ) : (
                            getSupplierName(order.supplier_id)
                          )}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {order.locked && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setNoteToAssign(order);
                            setAssignSupplierDialogOpen(true);
                          }}
                          className="text-green-600 hover:text-green-700 text-xs font-medium"
                          title="Lieferant zuordnen"
                        >
                          <UserCog className="h-4 w-4 mr-1" />
                          Zuordnen
                        </Button>
                      )}
                      {!order.locked && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePrintA4(order)}
                            className="text-purple-600 hover:text-purple-700"
                            title="A4 Bestellung drucken"
                            data-testid={`print-a4-btn-${order.id}`}
                          >
                            <Printer className="h-4 w-4" />
                          </Button>
                          {featureSettings.qr_label_printing_enabled && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedNoteForLabel(order);
                                setLabelPrintDialogOpen(true);
                              }}
                              className="text-blue-600 hover:text-blue-700 text-xs font-medium"
                              title="QR-Code Etikett drucken"
                              data-testid={`print-label-btn-${order.id}`}
                            >
                              QR Code
                            </Button>
                          )}
                        </>
                      )}
                      {/* Unterschrift rückgängig Button - nur für Admins bei unterschriebenen Bestellungen */}
                      {order.signed && user?.role === 'admin' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setNoteToUnsign(order);
                            setUnsignDialogOpen(true);
                          }}
                          className="text-orange-600 hover:text-orange-700"
                          title="Unterschrift rückgängig machen"
                          data-testid={`unsign-btn-${order.id}`}
                        >
                          <Undo2 className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setNoteToDelete(order);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-red-600 hover:text-red-700"
                        data-testid={`delete-note-btn-${order.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className={`font-medium ${order.signed ? 'text-green-600' : 'text-orange-600'}`}>
                        {order.signed ? 'Unterschrieben' : 'Ausstehend'}
                      </span>
                    </div>
                    {order.project_number && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Projekt-Nr.:</span>
                        <span className="font-medium text-blue-600">{order.project_number}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Hochgeladen:</span>
                      <span className="text-sm">{formatDate(order.uploaded_at)}</span>
                    </div>
                    {order.signed && order.signed_at && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Unterschrieben:</span>
                          <span className="text-sm">{formatDate(order.signed_at)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Von:</span>
                          <span className="text-sm font-medium">{order.signer_name}</span>
                        </div>
                      </>
                    )}
                  </div>
                  {order.locked ? (
                    <Button
                      className="w-full bg-gray-500 hover:bg-gray-600 text-white"
                      size="sm"
                      onClick={() => navigate(`/orders/${order.id}/sign`)}
                      data-testid={`view-btn-${order.id}`}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      Nur Anzeigen
                    </Button>
                  ) : !order.signed && (
                    <Button
                      className="w-full metaxon-primary text-white"
                      size="sm"
                      onClick={() => navigate(`/orders/${order.id}/sign`)}
                      data-testid={`sign-btn-${order.id}`}
                    >
                      <Eye className="mr-2 h-4 w-4" />
                      Anzeigen & Unterschreiben
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {filteredNotes.length > 0 && (
          <div className="text-center text-sm text-gray-600">
            {filteredNotes.length} von {orders.length} Bestellungen angezeigt
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-note-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Bestellung wirklich löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie den Bestellung "{noteToDelete?.filename}" wirklich löschen? 
              Diese Aktion kann nicht rückgängig gemacht werden.
              {noteToDelete?.signed && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-800 font-medium">
                    ⚠️ Hinweis: Dieser Bestellung wurde bereits unterschrieben.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-note-btn">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteNote}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-note-btn"
            >
              Bestellung löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unsign Confirmation Dialog */}
      <AlertDialog open={unsignDialogOpen} onOpenChange={setUnsignDialogOpen}>
        <AlertDialogContent data-testid="unsign-note-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Unterschrift rückgängig machen?</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie die Unterschrift für die Bestellung "{noteToUnsign?.filename}" wirklich rückgängig machen?
              <div className="mt-4 p-3 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-800 font-medium">
                  ⚠️ Hinweis: Die Unterschrift und der Stempel werden entfernt. 
                  Das Original-PDF wird wiederhergestellt.
                </p>
              </div>
              {noteToUnsign?.signer_name && (
                <div className="mt-3 text-sm">
                  <span className="text-gray-600">Unterschrieben von: </span>
                  <span className="font-medium">{noteToUnsign.signer_name}</span>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-unsign-btn">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnsignNote}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="confirm-unsign-btn"
            >
              Unterschrift rückgängig machen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Paper Delivery Note Requirement Alert */}
      <AlertDialog open={paperRequirementDialogOpen} onOpenChange={setPaperRequirementDialogOpen}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-orange-100 rounded-full">
                <Printer className="h-8 w-8 text-orange-600" />
              </div>
              <div>
                <AlertDialogTitle className="text-2xl">Papier-Bestellung erforderlich</AlertDialogTitle>
                <AlertDialogDescription className="text-base mt-1">
                  {suppliersRequiringPaper.length === 1 
                    ? 'Folgender Lieferant benötigt einen gedruckten Bestellung:'
                    : `${suppliersRequiringPaper.length} Lieferantn benötigen gedruckte Bestellunge:`
                  }
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          
          <div className="my-4">
            {suppliersRequiringPaper.length === 1 ? (
              <div className="p-4 bg-orange-50 border-2 border-orange-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-6 w-6 text-orange-600 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-lg text-orange-900">
                      {suppliersRequiringPaper[0].company_name}
                    </p>
                    {suppliersRequiringPaper[0].contact_person && (
                      <p className="text-sm text-orange-700">
                        Ansprechpartner: {suppliersRequiringPaper[0].contact_person}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto space-y-2 border rounded-lg p-3 bg-gray-50">
                {suppliersRequiringPaper.map((supplier, index) => (
                  <div 
                    key={supplier.id} 
                    className="p-3 bg-white border border-orange-200 rounded-lg flex items-start gap-3"
                  >
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <span className="text-orange-700 font-bold text-sm">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{supplier.company_name}</p>
                      {supplier.contact_person && (
                        <p className="text-sm text-gray-600">
                          Ansprechpartner: {supplier.contact_person}
                        </p>
                      )}
                    </div>
                    <Printer className="h-5 w-5 text-orange-500 flex-shrink-0" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              💡 <strong>Hinweis:</strong> Bitte stellen Sie sicher, dass die gedruckten Bestellunge an die entsprechenden Lieferantn übergeben werden.
            </p>
          </div>

          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={() => setPaperRequirementDialogOpen(false)}
              className="bg-orange-600 hover:bg-orange-700 w-full sm:w-auto"
            >
              Verstanden
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Label Print Dialog */}
      <LabelPrintDialog
        open={labelPrintDialogOpen}
        onOpenChange={setLabelPrintDialogOpen}
        order={selectedNoteForLabel}
        token={token}
      />


      {/* Assign Supplier Dialog */}
      <Dialog open={assignSupplierDialogOpen} onOpenChange={setAssignSupplierDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lieferant zuordnen</DialogTitle>
            <DialogDescription>
              Ordnen Sie den Bestellung "{noteToAssign?.filename}" einem Lieferantn zu.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssignSupplier} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="assign-supplier">Lieferant auswählen *</Label>
              <Select value={assignSupplierId} onValueChange={setAssignSupplierId} required>
                <SelectTrigger>
                  <SelectValue placeholder="Lieferant wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.filter(c => c.company_name && c.company_name.trim()).map(supplier => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => {
                setAssignSupplierDialogOpen(false);
                setNoteToAssign(null);
                setAssignSupplierId('');
              }}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={isLoading || !assignSupplierId}>
                {isLoading ? 'Wird zugeordnet...' : 'Zuordnen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </Layout>
  );
}
